import { ReactNode } from 'react';
import PaddingBox from '@uds/padding-box';
import { Panel } from '@uds/panel';
import Colorway, { Colorways } from '@uds/colorway';
import CallToAction from 'components/common/CallToAction';
import { CTA } from 'core/ribbons/cta';
import { ImageAspectRatio } from 'domain/constants/imageAspectRatio';
import { Behavior } from 'domain/constants/callToAction';

import CardImage from './CardImage';
import getClassNames, { StyledRoot } from './Card.stylesheet';

type ImageType = {
  imageUrl: string | undefined;
  illustration: string | undefined;
  alt?: string;
  imageAspectRatio?: ImageAspectRatio;
};

interface CardProps {
  ctas: Array<CTA> | undefined;
  ribbonId: string;
  colorway: Colorways;
  image: ImageType;
  footer?: ReactNode;
  children: ReactNode;
  last?: boolean;
}

const hasImage = (image: ImageType | null) => Boolean(image?.imageUrl || image?.illustration);

const getCtaUrl = (ctas: Array<CTA> | undefined) => {
  const cta = ctas?.[0];
  if (cta?.behavior?.type === Behavior.NAVIGATION) {
    return cta?.behavior?.goto;
  }

  return null;
};

const hasPanelBorder = (colorway: Colorways) => {
  return [Colorways.PRIMARY, Colorways.PRIMARY_GRADIENT, Colorways.DARK, Colorways.HIGHLIGHT].includes(colorway);
};

const Card = ({ image, children, footer, ctas = [], ribbonId, colorway, last }: CardProps) => {
  const ctaUrl = getCtaUrl(ctas);
  const showBorder = !hasPanelBorder(colorway);

  const classNames = getClassNames({
    hasMultipleCtas: ctas.length > 1,
    colorway,
  });

  return (
    <StyledRoot last={last}>
      <Panel border={showBorder} className={classNames.panel}>
        {hasImage(image) && <CardImage ctaUrl={ctaUrl ?? undefined} {...image} />}
        <Colorway
          colorway={colorway === Colorways.LIGHT ? Colorways.NEUTRAL : Colorways.LIGHT}
          className={classNames.content}
        >
          <PaddingBox>
            <div className={classNames.body}>{children}</div>
            <footer>
              {footer}
              {ctas.length > 0 && (
                <div className={classNames.ctas}>
                  {ctas.map((cta, i) => (
                    <div key={ribbonId ?? `cta-${i}`} className={classNames.cta}>
                      <CallToAction ribbonId={ribbonId} index={i} {...cta} />
                    </div>
                  ))}
                </div>
              )}
            </footer>
          </PaddingBox>
        </Colorway>
      </Panel>
    </StyledRoot>
  );
};

Card.displayName = 'Card';

// eslint-disable-next-line no-underscore-dangle
export const __test__ = {
  getCtaUrl,
  hasImage,
};

export default Card;
